import React, { useEffect, useState } from "react";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Breadcrumb from "../../components/common/Breadcrumb/Breadcrumb";
import MediaProgressSection from "./MediaProgressSection";
import VendorProductsSection from "./VendorProductsSection";
import WhereProductsGoSection from "./WhereProductsGoSection";
import CampaignUpdatesSection from "./CampaignUpdatesSection";
import ClosedCampaign from "../../components/common/CampaignDetailPageComponents/ClosedCampaign";
import { getVendorById, getVendors } from "../../api/services/vendorService";
import { canViewPrivateCampaign } from "../../utils/authUtils";

const isActiveStatus = (value) => value === true || Number(value) === 1;
const normalizeSlug = (value) =>
  decodeURIComponent(String(value || ""))
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
const extractVendorPayload = (response) => response?.data?.data || response?.data || response || null;
const extractVendorList = (response) => {
  const payload = response?.data?.data || response?.data || response;
  return Array.isArray(payload) ? payload : [];
};

const SmallVendorsDetailPage = () => {
  const navigate = useNavigate();
  const { id, slug } = useParams();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery("(max-width: 767.95px)");
  const isPrivateVendor = Number(vendor?.is_private) === 1;
  const canAccessPrivateVendor = canViewPrivateCampaign(user, isAuthenticated);
  const isPrivateVendorBlocked = isPrivateVendor && !canAccessPrivateVendor;
  const isClosedVendor = Number(vendor?.closed_vendor) === 1;
  const isUnavailableVendor = isPrivateVendorBlocked || isClosedVendor;
  const breadcrumbItems = [
    { label: "Small Vendor", path: "/small-vendors" },
    { label: "Vendor Detail Page" },
  ];

  useEffect(() => {
    let mounted = true;

    const fetchVendor = async () => {
      try {
        setLoading(true);
        const requestedKey = slug || id;
        if (requestedKey) {
          const isNumericId = /^\d+$/.test(String(requestedKey));
          if (isNumericId) {
            const response = await getVendorById(requestedKey);
            const detail = extractVendorPayload(response);
            if (!mounted) return;
            setVendor(detail && isActiveStatus(detail.status) ? detail : null);
          } else {
            const response = await getVendors();
            const list = extractVendorList(response);
            const requestedSlug = normalizeSlug(requestedKey);
            const matchedVendor = list.find((item) => {
              if (!item || !isActiveStatus(item.status)) return false;
              const redirects = Array.isArray(item.redirects) ? item.redirects : [];
              return redirects.some((r) => {
                const primary = normalizeSlug(r?.primary_url);
                const secondary = normalizeSlug(r?.secondary_url);
                const secondaryVisible = !(r?.secondaryUrlHide === 1 || r?.secondaryUrlHide === true);
                return primary === requestedSlug || (secondaryVisible && secondary === requestedSlug);
              });
            });
            if (!matchedVendor?.id) {
              if (!mounted) return;
              setVendor(null);
              return;
            }
            const detailResponse = await getVendorById(matchedVendor.id);
            const detail = extractVendorPayload(detailResponse);
            if (!mounted) return;
            setVendor(detail && isActiveStatus(detail.status) ? detail : null);
          }
        } else {
          const response = await getVendors();
          const list = extractVendorList(response);
          const firstActive = list.find((item) => item && isActiveStatus(item.status));
          if (!mounted) return;
          setVendor(firstActive || null);
        }
      } catch (error) {
        if (!mounted) return;
        setVendor(null);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    };

    fetchVendor();
    return () => {
      mounted = false;
    };
  }, [id, slug]);

  if (loading) {
    return (
      <>
        <Breadcrumb items={breadcrumbItems} />
        <Box sx={{ width: "94.44%", maxWidth: "1440px", mx: "auto", px: { xs: "16px", md: 0 }, py: "40px" }}>
          <Typography sx={{ fontSize: "16px", color: "#686866" }}>Loading vendor details...</Typography>
        </Box>
      </>
    );
  }

  if (!vendor) {
    return (
      <>
        <Breadcrumb items={breadcrumbItems} />
        <Box sx={{ width: "94.44%", maxWidth: "1440px", mx: "auto", px: { xs: "16px", md: 0 }, py: "40px" }}>
          <Typography sx={{ fontSize: "16px", color: "#686866" }}>Vendor details are currently unavailable.</Typography>
        </Box>
      </>
    );
  }

  if (isPrivateVendorBlocked) {
    return (
      <>
        <Breadcrumb items={breadcrumbItems} />
        <Box sx={{ width: "94.44%", maxWidth: "1440px", mx: "auto", px: { xs: "16px", md: 0 }, py: "40px" }}>
          <Typography sx={{ fontSize: "16px", color: "#686866" }}>Vendor details are currently unavailable.</Typography>
        </Box>
      </>
    );
  }

  if (isClosedVendor) {
    const closedCampaignData = {
      id: vendor?.id,
      backers: Number(vendor?.supporters ?? vendor?.supporters_count ?? 0),
      raisedAmount: Number(vendor?.raised_amount ?? 0),
      campaign_type: "campaign",
    };
    return (
      <>
        <Breadcrumb items={breadcrumbItems} />
        <ClosedCampaign campaignData={closedCampaignData} hideSimilarCampaigns={true} />
      </>
    );
  }

  return (
    <>
      <Breadcrumb
        items={breadcrumbItems}
      />
      <MediaProgressSection vendor={vendor} />
      <VendorProductsSection vendor={vendor} isUnavailableVendor={isUnavailableVendor} />
      <WhereProductsGoSection vendor={vendor} />
      <CampaignUpdatesSection vendor={vendor} />
      {isMobile && <Box sx={{ height: "86px" }} />}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1200,
            background: "#FFFFFF",
            boxShadow: "0px -4px 16px rgba(0, 0, 0, 0.1)",
            px: "10px",
            py: "16px",
          }}
        >
          <Button
            variant="contained"
            onClick={() => {
              if (isUnavailableVendor) return;
              navigate(`/small-vendors/payment/${vendor?.id}`);
            }}
            disabled={isUnavailableVendor}
            sx={{
              width: "100%",
              height: "46px",
              borderRadius: "6.233px",
              background: "#F04951",
              boxShadow: "0px 2.493px 0px 0px #141414",
              textTransform: "none",
              fontWeight: 700,
              fontSize: "14px",
              lineHeight: "8.726px",
              color: "#FFFFFF",
              "&:hover": { background: "#F04951" },
              "&.Mui-disabled": {
                color: "#FFFFFF",
                opacity: 0.65,
                background: "#F04951",
              },
            }}
          >
            {isClosedVendor ? "Vendor Closed" : "Buy Now"}
          </Button>
        </Box>
      )}
    </>
  );
};

export default SmallVendorsDetailPage;
